/**
 * Input/Output model utilities (DecisionRules-style I/O models for agents).
 *
 * - The OUTPUT model is stored as a standard JSON Schema string
 *   (AgentDefinition.OutputSchemaJson). `schemaToTree` / `treeToSchema`
 *   convert between that schema and an editable field tree, preserving
 *   unrecognized schema keywords (enum, minimum, pattern, ...) in `extra`
 *   so hand-written schemas survive a round-trip.
 * - The INPUT model is stored as flat AgentDefinitionParameter rows whose
 *   keys may be dot-paths ("patient.age"). Only leaf fields are persisted;
 *   object parents exist only in the tree.
 */

import type { AgentParameterDto, UpdateAgentParameterRequest } from '../types/agent';

/** Editable field types. `auto` means "no declared type" (model decides). */
export type IoFieldType =
  | 'text'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'date'
  | 'object'
  | 'array'
  | 'json'
  | 'auto';

/** A node in the I/O model tree. */
export interface IoField {
  id: string;
  name: string;
  type: IoFieldType;
  description?: string;
  required?: boolean;
  /** Children for `object` nodes (and `array` nodes whose items are objects). */
  children?: IoField[];
  /** For `array` nodes without object items: the item type. */
  itemType?: Exclude<IoFieldType, 'object' | 'array'>;
  /** Unrecognized JSON Schema keywords preserved for round-trip (output model). */
  extra?: Record<string, unknown>;
  /** Extra keywords on an array's `items` schema (output model round-trip). */
  itemsExtra?: Record<string, unknown>;
  /** Input model only: existing AgentDefinitionParameter id. */
  paramId?: string;
  /** Input model only: display label (defaults to the field name). */
  label?: string;
  /** Input model only: default value (or the secret value to store). */
  defaultValue?: string;
  /** Input model only: secret flag (stored in Key Vault). */
  isSecret?: boolean;
  /** Input model only: whether a Key Vault secret is already configured. */
  hasSecretConfigured?: boolean;
  /** Input model only: extra validation JSON ({min,max,pattern}). */
  validationJson?: string;
}

/** Result of parsing an output schema into a tree. */
export interface SchemaTreeResult {
  fields: IoField[];
  /** Root-level keywords other than type/properties/required (e.g. $schema). */
  rootExtra: Record<string, unknown>;
  /** False when the schema root cannot be represented as an object tree. */
  supported: boolean;
}

/** Field names: letters, digits, underscore and hyphen (DecisionRules rule). */
export const FIELD_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

/** Returns true when a field name is valid. */
export const isValidFieldName = (name: string): boolean => FIELD_NAME_PATTERN.test(name);

let idCounter = 0;
/** Generates a stable-enough unique id for tree nodes. */
export const newFieldId = (): string => `iofield-${Date.now().toString(36)}-${++idCounter}`;

const SCALAR_TYPE_TO_SCHEMA: Record<string, string | undefined> = {
  text: 'string',
  number: 'number',
  integer: 'integer',
  boolean: 'boolean',
  date: 'string',
  json: 'object',
  auto: undefined,
};

const omit = (obj: Record<string, unknown>, keys: string[]): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (!keys.includes(k)) result[k] = v;
  }
  return result;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Converts one JSON Schema property into a tree node. */
const schemaPropertyToField = (name: string, schema: unknown, required: boolean): IoField => {
  const field: IoField = { id: newFieldId(), name, type: 'auto', required };

  if (!isPlainObject(schema)) {
    return field;
  }

  const type = schema['type'];
  const description = typeof schema['description'] === 'string' ? (schema['description'] as string) : undefined;
  if (description !== undefined) field.description = description;

  if (type === 'object' || (type === undefined && isPlainObject(schema['properties']))) {
    field.type = 'object';
    const properties = isPlainObject(schema['properties']) ? schema['properties'] : {};
    const requiredList = Array.isArray(schema['required']) ? (schema['required'] as unknown[]) : [];
    field.children = Object.entries(properties).map(([childName, childSchema]) =>
      schemaPropertyToField(childName, childSchema, requiredList.includes(childName)),
    );
    const extra = omit(schema, ['type', 'properties', 'required', 'description']);
    if (Object.keys(extra).length > 0) field.extra = extra;
    return field;
  }

  if (type === 'array') {
    field.type = 'array';
    const items = schema['items'];
    if (isPlainObject(items) && (items['type'] === 'object' || isPlainObject(items['properties']))) {
      const properties = isPlainObject(items['properties']) ? items['properties'] : {};
      const requiredList = Array.isArray(items['required']) ? (items['required'] as unknown[]) : [];
      field.children = Object.entries(properties).map(([childName, childSchema]) =>
        schemaPropertyToField(childName, childSchema, requiredList.includes(childName)),
      );
      const itemsExtra = omit(items, ['type', 'properties', 'required']);
      if (Object.keys(itemsExtra).length > 0) field.itemsExtra = itemsExtra;
    } else if (isPlainObject(items)) {
      const itemType = items['type'];
      if (itemType === 'string' && items['format'] === 'date') {
        field.itemType = 'date';
        const itemsExtra = omit(items, ['type', 'format']);
        if (Object.keys(itemsExtra).length > 0) field.itemsExtra = itemsExtra;
      } else {
        field.itemType =
          itemType === 'string' ? 'text'
          : itemType === 'number' ? 'number'
          : itemType === 'integer' ? 'integer'
          : itemType === 'boolean' ? 'boolean'
          : 'auto';
        const itemsExtra = omit(items, ['type']);
        if (Object.keys(itemsExtra).length > 0) field.itemsExtra = itemsExtra;
      }
    }
    const extra = omit(schema, ['type', 'items', 'description']);
    if (Object.keys(extra).length > 0) field.extra = extra;
    return field;
  }

  if (type === 'string' && schema['format'] === 'date') {
    field.type = 'date';
    const extra = omit(schema, ['type', 'format', 'description']);
    if (Object.keys(extra).length > 0) field.extra = extra;
    return field;
  }

  field.type =
    type === 'string' ? 'text'
    : type === 'number' ? 'number'
    : type === 'integer' ? 'integer'
    : type === 'boolean' ? 'boolean'
    : 'auto';

  const extraKeys = field.type === 'auto' ? ['description'] : ['type', 'description'];
  const extra = omit(schema, extraKeys);
  if (Object.keys(extra).length > 0) field.extra = extra;
  return field;
};

/**
 * Parses an OutputSchemaJson string into an editable tree.
 * Returns supported=false when the root is not an object schema
 * (the caller should fall back to raw JSON editing).
 */
export const schemaToTree = (schemaJson: string | undefined | null): SchemaTreeResult => {
  const empty: SchemaTreeResult = { fields: [], rootExtra: {}, supported: true };
  const trimmed = (schemaJson ?? '').trim();
  if (!trimmed || trimmed === '{}') return empty;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { fields: [], rootExtra: {}, supported: false };
  }

  if (!isPlainObject(parsed)) return { fields: [], rootExtra: {}, supported: false };

  const rootType = parsed['type'];
  const hasProperties = isPlainObject(parsed['properties']);
  if (rootType !== undefined && rootType !== 'object') return { fields: [], rootExtra: {}, supported: false };
  if (rootType === undefined && !hasProperties) {
    // An object without type/properties: representable only if it has no other keys.
    if (Object.keys(parsed).length === 0) return empty;
    return { fields: [], rootExtra: {}, supported: false };
  }

  const properties = hasProperties ? (parsed['properties'] as Record<string, unknown>) : {};
  const requiredList = Array.isArray(parsed['required']) ? (parsed['required'] as unknown[]) : [];
  const fields = Object.entries(properties).map(([name, schema]) =>
    schemaPropertyToField(name, schema, requiredList.includes(name)),
  );
  const rootExtra = omit(parsed, ['type', 'properties', 'required']);
  return { fields, rootExtra, supported: true };
};

/** Converts one tree node back into a JSON Schema property. */
const fieldToSchemaProperty = (field: IoField): Record<string, unknown> => {
  const schema: Record<string, unknown> = {};

  if (field.type === 'object') {
    schema['type'] = 'object';
    if (field.description) schema['description'] = field.description;
    const properties: Record<string, unknown> = {};
    const required: string[] = [];
    for (const child of field.children ?? []) {
      properties[child.name] = fieldToSchemaProperty(child);
      if (child.required) required.push(child.name);
    }
    schema['properties'] = properties;
    if (required.length > 0) schema['required'] = required;
    if (field.extra) Object.assign(schema, field.extra);
    return schema;
  }

  if (field.type === 'array') {
    schema['type'] = 'array';
    if (field.description) schema['description'] = field.description;
    if (field.children && field.children.length > 0) {
      const properties: Record<string, unknown> = {};
      const required: string[] = [];
      for (const child of field.children) {
        properties[child.name] = fieldToSchemaProperty(child);
        if (child.required) required.push(child.name);
      }
      const items: Record<string, unknown> = { type: 'object', properties };
      if (required.length > 0) items['required'] = required;
      if (field.itemsExtra) Object.assign(items, field.itemsExtra);
      schema['items'] = items;
    } else if (field.itemType || field.itemsExtra) {
      const items: Record<string, unknown> = {};
      const mapped = field.itemType ? SCALAR_TYPE_TO_SCHEMA[field.itemType] : undefined;
      if (mapped) items['type'] = mapped;
      if (field.itemType === 'date') items['format'] = 'date';
      if (field.itemsExtra) Object.assign(items, field.itemsExtra);
      schema['items'] = items;
    }
    if (field.extra) Object.assign(schema, field.extra);
    return schema;
  }

  const mappedType = SCALAR_TYPE_TO_SCHEMA[field.type];
  if (mappedType) schema['type'] = mappedType;
  if (field.type === 'date') schema['format'] = 'date';
  if (field.description) schema['description'] = field.description;
  if (field.extra) Object.assign(schema, field.extra);
  return schema;
};

/** Serializes a tree back to an OutputSchemaJson string. */
export const treeToSchema = (fields: IoField[], rootExtra: Record<string, unknown> = {}): string => {
  if (fields.length === 0 && Object.keys(rootExtra).length === 0) return '{}';

  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  for (const field of fields) {
    properties[field.name] = fieldToSchemaProperty(field);
    if (field.required) required.push(field.name);
  }

  const schema: Record<string, unknown> = { ...rootExtra, type: 'object', properties };
  if (required.length > 0) schema['required'] = required;
  return JSON.stringify(schema, null, 2);
};

const PARAM_TYPE_TO_FIELD: Record<string, IoFieldType> = {
  string: 'text',
  text: 'text',
  number: 'number',
  int: 'integer',
  integer: 'integer',
  double: 'number',
  float: 'number',
  decimal: 'number',
  boolean: 'boolean',
  bool: 'boolean',
  date: 'date',
  json: 'json',
  object: 'json',
  array: 'array',
};

const FIELD_TYPE_TO_PARAM: Record<string, string> = {
  text: 'string',
  number: 'number',
  integer: 'integer',
  boolean: 'boolean',
  date: 'date',
  json: 'json',
  array: 'array',
  auto: 'string',
};

/** Maps a stored parameter type to a tree field type. */
export const paramTypeToFieldType = (type: string): IoFieldType =>
  PARAM_TYPE_TO_FIELD[type?.toLowerCase()] ?? 'text';

/** Maps a tree field type back to a stored parameter type. */
export const fieldTypeToParamType = (type: IoFieldType): string => FIELD_TYPE_TO_PARAM[type] ?? 'string';

/**
 * Builds a tree from flat AgentDefinitionParameter rows, grouping dot-path
 * keys ("patient.age") under object parent nodes.
 */
export const parametersToTree = (parameters: AgentParameterDto[]): IoField[] => {
  const roots: IoField[] = [];

  const findOrCreateGroup = (list: IoField[], name: string): IoField => {
    let group = list.find((f) => f.name === name && f.type === 'object');
    if (!group) {
      group = { id: newFieldId(), name, type: 'object', children: [] };
      list.push(group);
    }
    group.children = group.children ?? [];
    return group;
  };

  for (const param of parameters) {
    const segments = param.key.split('.').filter((s) => s.length > 0);
    if (segments.length === 0) continue;

    let list = roots;
    for (let i = 0; i < segments.length - 1; i++) {
      list = findOrCreateGroup(list, segments[i]).children!;
    }

    const leafName = segments[segments.length - 1];
    list.push({
      id: newFieldId(),
      name: leafName,
      type: paramTypeToFieldType(param.type),
      description: param.description,
      required: param.isRequired,
      paramId: param.id,
      label: param.label && param.label !== leafName ? param.label : undefined,
      defaultValue: param.defaultValue ?? undefined,
      isSecret: param.isSecret,
      hasSecretConfigured: param.hasSecretConfigured,
      validationJson: param.validationJson ?? undefined,
    });
  }

  return roots;
};

/** A flattened input-model leaf ready to submit as an agent parameter. */
export interface InputParameterRow extends UpdateAgentParameterRequest {
  sortOrder: number;
}

/**
 * Flattens the input-model tree into AgentDefinitionParameter rows.
 * Only leaves are persisted; object parents contribute their name to the
 * dot-path key. SortOrder follows depth-first tree order.
 */
export const treeToParameters = (fields: IoField[]): InputParameterRow[] => {
  const rows: InputParameterRow[] = [];
  let order = 0;

  const visit = (list: IoField[], prefix: string) => {
    for (const field of list) {
      const path = prefix ? `${prefix}.${field.name}` : field.name;
      if (field.type === 'object') {
        visit(field.children ?? [], path);
        continue;
      }

      rows.push({
        id: field.paramId,
        key: path,
        label: field.label?.trim() || field.name,
        description: field.description || undefined,
        type: fieldTypeToParamType(field.type),
        defaultValue: field.defaultValue || undefined,
        isRequired: field.required ?? false,
        isSecret: field.isSecret ?? false,
        validationJson: field.validationJson || undefined,
        sortOrder: order++,
      });
    }
  };

  visit(fields, '');
  return rows;
};

/**
 * Validates a tree: every name must match FIELD_NAME_PATTERN and sibling
 * names must be unique. Returns human-readable problems (empty = valid).
 */
export const validateTree = (fields: IoField[]): string[] => {
  const problems: string[] = [];

  const visit = (list: IoField[], prefix: string) => {
    const seen = new Set<string>();
    for (const field of list) {
      const path = prefix ? `${prefix}.${field.name}` : field.name;
      if (!field.name.trim()) {
        problems.push(`A field under '${prefix || 'root'}' has an empty name.`);
      } else if (!isValidFieldName(field.name)) {
        problems.push(`Field name '${path}' is invalid. Use letters, numbers, underscores and hyphens only.`);
      }
      const nameKey = field.name.toLowerCase();
      if (seen.has(nameKey)) {
        problems.push(`Duplicate field name '${path}'.`);
      }
      seen.add(nameKey);
      if (field.children && field.children.length > 0) {
        visit(field.children, path);
      }
    }
  };

  visit(fields, '');
  return problems;
};

/** Collects the dot-paths of all leaf fields (usable as {{variables}}). */
export const collectLeafPaths = (fields: IoField[]): string[] => {
  const paths: string[] = [];
  const visit = (list: IoField[], prefix: string) => {
    for (const field of list) {
      const path = prefix ? `${prefix}.${field.name}` : field.name;
      if (field.type === 'object') {
        visit(field.children ?? [], path);
      } else {
        paths.push(path);
      }
    }
  };
  visit(fields, '');
  return paths;
};

/** Extracts {{variable}} references from a Mustache template. */
export const extractTemplateVariables = (template: string): string[] => {
  const pattern = /\{\{[#/^]?\s*([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)\s*\}\}/g;
  const variables = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(template)) !== null) {
    variables.add(match[1]);
  }
  return [...variables];
};
